import {
  type IdentifierAssociationsReportRequest,
  type IdentifierAssociationsRequest,
  IdentifierAssociationsRequester,
  type IdentifierAssociationsResponse,
  IdentifierTokensGraphQLRequester,
} from '@cobliteam/api-lib';
import type { IdentificationTokenV2 } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();
const identifierTokensGraphQLRequester = () => new IdentifierTokensGraphQLRequester();

// Lazy instantiation to avoid circular dependency issues
const getIdentifierAssociationsRequester = () => new IdentifierAssociationsRequester({ host: env.apiHost });

const QUERY_KEY = 'IDENTIFIER_ASSOCIATIONS';
const TOKEN_QUERY_KEY = 'IDENTIFICATION_TOKEN';

const useIdentifierAssociationsRows = (request: IdentifierAssociationsRequest) => {
  return useInfiniteQuery<IdentifierAssociationsResponse>({
    initialPageParam: 0,
    queryKey: [QUERY_KEY, 'ROWS', JSON.stringify(request)],
    queryFn: ({ pageParam = 0 }) =>
      getIdentifierAssociationsRequester().getIdentifierAssociations({
        ...request,
        page: pageParam as number,
        size: 100,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useRequestIdentifierAssociationsReport = () =>
  useMutation<string, unknown, { request: IdentifierAssociationsReportRequest }>({
    mutationFn: (variables) => getIdentifierAssociationsRequester().requestIdentifierAssociationsReport(variables.request),
  });

const getNextPageParam = (lastPage: IdentifierAssociationsResponse, allPages: IdentifierAssociationsResponse[]) => {
  const loadedItems = allPages
    .flat()
    .map((response) => response.rows.length)
    .reduce((acc, a) => acc + a);

  if (lastPage.count === loadedItems) return undefined;
  return allPages.flat().length;
};

const useIdentificationToken = () => {
  const queryClient = useQueryClient();

  const getIdentificationTokens = (filters?: Partial<{ driverIds?: string[]; ids?: string[]; isLinkedToDriver?: boolean }>) => {
    return useQuery<IdentificationTokenV2[]>({
      queryKey: [TOKEN_QUERY_KEY, 'unbound', filters],
      queryFn: () => identifierTokensGraphQLRequester().getIdentificationTokens(filters),
    });
  };

  const associateTokenToDriver = () => {
    return useMutation({
      mutationFn: async ({ driverId, tokenId }: { driverId: UUID; tokenId: string }) => identifierTokensGraphQLRequester().associateIdentificationTokenToDriver(driverId, tokenId),
      onSuccess: () => {
        Alert.success('Identificador associado com sucesso!');
        queryClient.invalidateQueries({ queryKey: [TOKEN_QUERY_KEY, 'unbound'] });

        queryClient.resetQueries({ queryKey: ['QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY'] });
        queryClient.resetQueries({ queryKey: ['IDENTIFICATION_TOKEN_V2'] });
      },
      onError: () => {
        Alert.error('Erro ao associar identificador!');
      },
    });
  };

  const disassociateTokenFromDriver = () => {
    return useMutation({
      mutationFn: async ({ tokenId }: { tokenId: string }) => identifierTokensGraphQLRequester().disassociateIdentificationTokenFromDriver(tokenId),
      onSuccess: () => {
        Alert.success('Identificador desassociado com sucesso!');
        queryClient.invalidateQueries({ queryKey: [TOKEN_QUERY_KEY, 'unbound'] });

        queryClient.resetQueries({ queryKey: ['QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY'] });
        queryClient.resetQueries({ queryKey: ['IDENTIFICATION_TOKEN_V2'] });
      },
      onError: () => {
        Alert.error('Erro ao desassociar identificador!');
      },
    });
  };

  const deleteToken = () => {
    return useMutation({
      mutationFn: async ({ tokenId }: { tokenId: string }) => identifierTokensGraphQLRequester().deleteIdentificationToken(tokenId),
      onSuccess: () => {
        Alert.success('Identificador removido com sucesso!');
      },
      onError: () => {
        Alert.error('Erro ao remover identificador!');
      },
    });
  };

  return {
    getIdentificationTokens,
    associateTokenToDriver,
    disassociateTokenFromDriver,
    deleteToken,
  };
};

export default {
  useIdentifierAssociationsRows,
  useRequestIdentifierAssociationsReport,
  useIdentificationToken,
};
